import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  assignOrdersToCourier,
  courierKeys,
  mapCourierError,
  useTranslation,
} from '@dukanti/shared';
import type { CourierAssignmentResult, TranslationKey } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface AssignOrdersToCourierVariables {
  readonly courierId: string;
  readonly orderIds: string[];
}

export function useAssignOrdersToCourier(
  storeId: string,
): UseMutationResult<CourierAssignmentResult, unknown, AssignOrdersToCourierVariables> {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<CourierAssignmentResult, unknown, AssignOrdersToCourierVariables>({
    mutationFn: ({ courierId, orderIds }) =>
      assignOrdersToCourier(supabase, {
        storeId,
        courierId,
        orderIds,
      }),
    onSuccess: (result, variables) => {
      if (!result.success) {
        toast.error(t(mapCourierError(undefined, result) as TranslationKey));
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: ['store-orders', storeId],
        refetchType: 'inactive',
      });
      void queryClient.invalidateQueries({
        queryKey: courierKeys.batches(storeId),
        refetchType: 'inactive',
      });
      for (const orderId of variables.orderIds) {
        void queryClient.invalidateQueries({
          queryKey: ['admin-order-detail', orderId],
          refetchType: 'inactive',
        });
      }
      toast.success(t('admin.orders.courierAssignment.linkGenerated' as TranslationKey));
    },
    onError: (err) => {
      toast.error(t(mapCourierError(err) as TranslationKey));
    },
  });
}
